import React from 'react';
import { BookMarked, CircleDashed, Ban, ScanSearch, FileCheck2, FlaskRound, ShieldCheck, BadgeCheck } from 'lucide-react';
import type { ValidationStatus } from '../../types/progression';
const styles: Record<ValidationStatus, {
  label: string;
  cls: string;
  Icon: typeof BookMarked;
}> = {
  identificado: {
    label: 'Identificado en el catálogo',
    cls: 'border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F3E5AB]',
    Icon: ScanSearch
  },
  documentado: {
    label: 'Documentado en los datos',
    cls: 'border-emerald-300/30 bg-emerald-300/10 text-emerald-200',
    Icon: FileCheck2
  },
  clasica: {
    label: 'Referencia clásica',
    cls: 'border-sky-300/30 bg-sky-300/10 text-sky-200',
    Icon: BookMarked
  },
  pendiente: {
    label: 'Pendiente de confirmar',
    cls: 'border-amber-300/30 bg-amber-300/10 text-amber-100',
    Icon: CircleDashed
  },
  'no-disponible': {
    label: 'No disponible',
    cls: 'border-red-300/30 bg-red-300/10 text-red-200',
    Icon: Ban
  },
  'sin-probar': {
    label: 'Sin probar en el juego',
    cls: 'border-white/15 bg-white/5 text-gray-300',
    Icon: FlaskRound
  },
  admin: {
    label: 'Confirmado por administración',
    cls: 'border-violet-300/30 bg-violet-300/10 text-violet-100',
    Icon: ShieldCheck
  },
  probado: {
    label: 'Comprobado dentro del juego',
    cls: 'border-emerald-300/40 bg-emerald-300/15 text-emerald-100',
    Icon: BadgeCheck
  }
};

/** Estado de validación de UN dato concreto (no de toda la ficha). */
export function ValidationBadge({
  status,
  label
}: {
  status: ValidationStatus;
  label?: string;
}) {
  const s = styles[status];
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${s.cls}`} data-id="element-1059" data-mp="15y2cshbx8dg">
      <s.Icon className="h-3 w-3" aria-hidden="true" data-id="element-1060" data-mp="63zmlgqhrdqi" />
      {label ?? s.label}
    </span>;
}
export type ValidationRow = {
  label: string;
  status: ValidationStatus;
  detail?: string;
};

/** Estado de la información de una guía, dimensión por dimensión. */
export function ValidationSummary({
  rows
}: {
  rows: ValidationRow[];
}) {
  return <details className="group rounded-2xl border border-white/10 bg-[#0A1628]" data-id="element-1061" data-mp="3f96av1q6s180">
      <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-2 px-3.5 py-2.5 text-sm font-semibold text-white" data-id="element-1062" data-mp="3ab4rsgsrg1wj">
        Estado de la información
        <span className="text-xs font-normal text-gray-400 group-open:hidden" data-id="element-1063" data-mp="3k92fn36pjwom">Ver detalle</span>
      </summary>
      <ul className="divide-y divide-white/5 border-t border-white/5" data-id="element-1064" data-mp="2367oskwlfbc">
        {rows.map(r => <li key={r.label} className="flex flex-col gap-1 px-3.5 py-2.5 sm:flex-row sm:items-center sm:justify-between" data-id="element-1065" data-mp="3ta0kfnoz0889">
            <span className="min-w-0" data-id="element-1066" data-mp="btiteo4gfej2">
              <span className="block text-sm text-gray-200" data-id="element-1067" data-mp="bsg42kms5k59">{r.label}</span>
              {r.detail && <span className="block text-xs text-gray-500" data-id="element-1068" data-mp="6uo73c7a37u7">{r.detail}</span>}
            </span>
            <ValidationBadge status={r.status} data-id="element-1069" data-mp="kuguaxjlj5xf" />
          </li>)}
      </ul>
    </details>;
}

/** Leyenda breve de los estados, para la cabecera de cada guía. */
export function ValidationLegend() {
  return <div className="rounded-2xl border border-white/10 bg-[#0A1628] p-3.5 text-sm text-gray-300 md:p-4" data-id="element-1070" data-mp="zejl2wf80xk0">
      <p className="text-gray-200" data-id="element-1071" data-mp="14dd3n39d7er1">Cada dato se valida por separado. Una receta o misión sigue siendo referencia clásica aunque sus objetos estén identificados.</p>
      <ul className="mt-2.5 space-y-1.5 text-xs" data-id="element-1072" data-mp="ug8gp17p3f96">
        <li className="flex flex-wrap items-center gap-2" data-id="element-1073" data-mp="gqep4b4t2gvr"><ValidationBadge status="identificado" data-id="element-1074" data-mp="gok2msdvygpk" /> el objeto existe en el catálogo; no confirma disponibilidad ni requisitos.</li>
        <li className="flex flex-wrap items-center gap-2" data-id="element-1075" data-mp="lioareyw0us8"><ValidationBadge status="documentado" data-id="element-1076" data-mp="6t08obivts3g" /> la ruta, ubicación o relación figura en los datos del servidor.</li>
        <li className="flex flex-wrap items-center gap-2" data-id="element-1077" data-mp="brtklziitb1i"><ValidationBadge status="clasica" data-id="element-1078" data-mp="3twom6iy97u3e" /> mecánica de Season 6 sin contrastar con Vanguard.</li>
        <li className="flex flex-wrap items-center gap-2" data-id="element-1079" data-mp="2qdlinx5ovcl"><ValidationBadge status="pendiente" data-id="element-1080" data-mp="2p2bw7h14tdk" /> falta ese dato concreto.</li>
        <li className="flex flex-wrap items-center gap-2" data-id="element-1081" data-mp="3qvbo2rwzg591"><ValidationBadge status="sin-probar" data-id="element-1082" data-mp="3qx66nzks7l9y" /> nada de esta guía se ha comprobado aún dentro del juego.</li>
      </ul>
    </div>;
}